<template>
  <div class="draggable-table">
    <header :style="{ width: `calc(100% - ${scrollBarWidth}px)` }">
      <template v-for="column in columns">
        <div v-if="!column.hidden" :key="column.key" :class="['cell', column.key]">
          <span>{{ column.name || column.key }}</span>
        </div>
      </template>
      <div class="cell handle">移動</div>
    </header>
    <LoadingIndicator v-if="loading" />
    <div v-else-if="!items.length" class="nodata">
      <slot name="nodata">{{ nodata }}</slot>
    </div>
    <Draggable v-else class="main" v-model="_items" handle=".handle" animation="150" scrollSpeed="10">
      <div v-for="item in items" :key="item.id" class="draggable-item">
        <template v-for="column in columns">
          <div v-if="!column.hidden" :key="column.key" :class="['cell', column.key]">
            <slot :name="column.key" :item="item">
              <span v-if="column.format">{{ column.format(item[column.key]) }}</span>
              <span v-else-if="column.filter">{{ column.filter(item) }}</span>
              <span v-else>{{ item[column.key] }}</span>
            </slot>
          </div>
        </template>
        <div class="cell handle"><MoveIcon /></div>
      </div>
    </Draggable>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import MoveIcon from '@/components/icons/move'

/**
 * 並べ替えテーブル
 */
export default {
  name: 'DraggableTable',
  components: {
    Draggable,
    LoadingIndicator,
    MoveIcon,
  },
  model: {
    prop: 'items',
    event: 'change',
  },
  props: {
    /** 列情報 */
    columns: Array,
    /** 行データ */
    items: { type: Array, default: () => [] },
    /** ローディング中フラグ */
    loading: Boolean,
    /** データがない場合に表示する文言 */
    nodata: { type: String, default: '該当するデータはありません' },
  },
  data() {
    return {
      scrollBarWidth: 0,
    }
  },
  computed: {
    _items: {
      get() {
        return this.items
      },
      set(items) {
        this.$emit('change', items)
      },
    },
  },
  updated() {
    this.$nextTick(() => {
      const main = this.$refs.main
      this.scrollBarWidth = main ? main.$el.offsetWidth - main.$el.clientWidth : 0
    })
  },
  methods: {},
}
</script>

<style lang="postcss" scoped>
.draggable-table {
  display: flex;
  flex-direction: column;
  min-height: 150px;
}

header {
  align-items: center;
  background: var(--very-dark-gray);
  border-right: 1px solid var(--white);
  box-sizing: border-box;
  color: var(--white);
  display: flex;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  position: sticky;
  top: 0;
}

.main {
  flex-grow: 1;
  overflow-y: auto;
}

.draggable-item {
  background: var(--white);
  border-bottom: 1px solid var(--border-color);
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  min-height: 56px;

  .handle {
    background-color: var(--mostly-white);
  }
}

.cell {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  line-height: 1;
  position: relative;
  white-space: nowrap;
  width: 100%;

  header > & {
    border-right: 1px solid var(--white);
    height: 32px;
    justify-content: center;

    &.sortable {
      cursor: pointer;
    }
  }

  .draggable-item > & {
    line-height: 1.4;
    padding: 0 24px;

    &::after {
      background-image: linear-gradient(
        to bottom,
        var(--dark-gray),
        var(--dark-gray) 1px,
        transparent 1px,
        transparent 4px
      );
      background-repeat: repeat-y;
      background-size: 1px 5px;
      content: '';
      height: 56px;
      position: absolute;
      right: 0;
      top: 1px;
      width: 1px;
    }
  }

  &.handle {
    border-right: none;
    cursor: pointer;
    fill: var(--dark-gray);
    justify-content: center;
    min-width: 52px;
    padding: 0;
    width: 52px;

    &::after {
      display: none;
    }
  }
}

.nodata {
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: center;
}
</style>
